import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Search, FileText, User as UserIcon, Activity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_COLORS = {
  LOGIN: { bg: '#F5E6F5', color: '#990099' },
  CREATE: { bg: '#D1FAE5', color: '#065F46' },
  UPDATE: { bg: '#DBEAFE', color: '#1E40AF' },
  DELETE: { bg: '#FEE2E2', color: '#991B1B' },
  HARD_DELETE: { bg: '#FEE2E2', color: '#991B1B' },
  ARCHIVE: { bg: '#FEF3C7', color: '#92400E' },
  RESTORE: { bg: '#D1FAE5', color: '#065F46' },
  DEACTIVATE: { bg: '#FEE2E2', color: '#991B1B' },
  ACTIVATE: { bg: '#D1FAE5', color: '#065F46' },
  BULK_DELETE: { bg: '#FEE2E2', color: '#991B1B' },
};

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (filterAction) params.append('action', filterAction);
      if (filterResource) params.append('resource', filterResource);
      const { data } = await axios.get(`${API}/audit-logs?${params}`);
      setLogs(data);
    } catch (e) {
      console.error('Error fetching audit logs:', e);
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterResource]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filtered = useMemo(() => {
    if (!search) return logs;
    const lower = search.toLowerCase();
    return logs.filter(
      (l) =>
        l.userName.toLowerCase().includes(lower) ||
        l.summary.toLowerCase().includes(lower) ||
        l.resourceId.toLowerCase().includes(lower)
    );
  }, [search, logs]);

  const formatDate = (ts) => new Date(ts).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit'
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="audit-logs-title">
          Audit Trail
        </h1>
        <p className="mt-2" style={{ color: '#6B7280' }}>Track all administrative actions and data changes</p>
      </div>

      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={20} style={{ color: '#6B7280' }} />
            <input
              type="text"
              placeholder="Search by user, summary, or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
              data-testid="audit-search-input"
            />
          </div>
          <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} data-testid="audit-action-filter">
            <option value="">All Actions</option>
            <option value="LOGIN">Login</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
            <option value="HARD_DELETE">Hard Delete</option>
            <option value="ARCHIVE">Archive</option>
            <option value="RESTORE">Restore</option>
            <option value="DEACTIVATE">Deactivate</option>
            <option value="ACTIVATE">Activate</option>
            <option value="BULK_DELETE">Bulk Delete</option>
          </select>
          <select value={filterResource} onChange={(e) => setFilterResource(e.target.value)} data-testid="audit-resource-filter">
            <option value="">All Resources</option>
            <option value="patient">Patient</option>
            <option value="consultation">Consultation</option>
            <option value="user">User</option>
            <option value="auth">Authentication</option>
          </select>
        </div>
      </div>

      <div className="card p-6">
        {loading ? (
          <div className="text-center py-12" style={{ color: '#6B7280' }}>Loading audit logs...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12" data-testid="no-audit-logs">
            <FileText size={48} style={{ color: '#6B7280', margin: '0 auto' }} />
            <p className="mt-4" style={{ color: '#6B7280' }}>No audit log entries match your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="audit-logs-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => {
                  const ac = ACTION_COLORS[log.action] || { bg: '#F5E6F5', color: '#6B7280' };
                  return (
                    <tr key={log.id} data-testid={`audit-row-${log.id}`}>
                      <td className="text-sm" style={{ color: '#6B7280' }}>{formatDate(log.timestamp)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <UserIcon size={14} style={{ color: '#990099' }} />
                          <span className="font-medium" style={{ color: '#2D3A2A' }}>{log.userName}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F5E6F5', color: '#990099' }}>{log.userRole}</span>
                        </div>
                      </td>
                      <td>
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: ac.bg, color: ac.color }}>
                          {log.action}
                        </span>
                      </td>
                      <td><span className="text-sm font-medium uppercase tracking-wide" style={{ color: '#78716C' }}>{log.resource}</span></td>
                      <td className="text-sm" style={{ color: '#2D3A2A' }}>{log.summary}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-center" style={{ color: '#6B7280' }}>
        Showing {filtered.length} of {logs.length} entries
        <Activity size={14} className="inline ml-2" />
      </div>
    </div>
  );
};

export default AuditLogs;
